import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Sparkles, Mic, Radio, BookOpen, Users, Calendar, Star, Settings2, Trash2, Edit3, Search, ChevronUp, ChevronDown, X, Shield, AlertTriangle, ShoppingBag, Mail, Heart, HeartHandshake } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { Markdown } from "../components/Markdown";
import { StorePanel } from "./AdminStore";
import { LearningPanel } from "./AdminLearning";
import { CommsPanel } from "./AdminComms";
import { GivingPanel } from "./AdminGiving";
import { HeartRequestsPanel } from "./AdminHeartRequests";

const STAGE_NAMES = ["ENTER", "REMEMBER", "EXPLORE", "DISCERN", "INTEGRATE", "EMBODY", "SYNTHESIZE", "RETURN"];

const MENU = [
  { id: "overview", label: "Overview", Icon: Sparkles },
  { id: "members", label: "Members", Icon: Users },
  { id: "teachings", label: "Teachings", Icon: Mic },
  { id: "courses", label: "Courses", Icon: BookOpen },
  { id: "library", label: "Library", Icon: BookOpen },
  { id: "events", label: "Events", Icon: Calendar },
  { id: "attendees", label: "Attendees", Icon: Users },
  { id: "radio", label: "Radio", Icon: Radio },
  { id: "featured", label: "Featured", Icon: Star },
  { id: "safety", label: "Safety Alerts", Icon: Shield },
  { id: "heart", label: "Heart Requests", Icon: HeartHandshake },
  { id: "store", label: "Store", Icon: ShoppingBag },
  { id: "comms", label: "Communications", Icon: Mail },
  { id: "giving", label: "Giving", Icon: Heart },
  { id: "settings", label: "Settings", Icon: Settings2 },
];

const inputCls = "w-full rounded-lg border px-3 py-2 text-sm bg-transparent";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest mb-1";

const Field = ({ label, children }) => (
  <label className="block mb-3">
    <span className={labelCls} style={{ color: "var(--gold-dark)" }}>{label}</span>
    {children}
  </label>
);

const Drawer = ({ open, onClose, title, children }) => open ? (
  <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(48,43,41,0.55)" }} onClick={onClose}>
    <div className="w-full max-w-2xl h-full overflow-y-auto p-6 sm:p-8" onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-cream)" }}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>{title}</h2>
        <button onClick={onClose} data-testid="drawer-close-btn" className="p-2 rounded-full hover:bg-[color:var(--burgundy-tint)]"><X className="w-4 h-4" /></button>
      </div>
      {children}
    </div>
  </div>
) : null;

const StatusBadge = ({ status }) => {
  const map = {
    published: { bg: "var(--gold)", fg: "var(--burgundy)" },
    draft: { bg: "var(--bg-cream)", fg: "var(--text-muted)" },
    archived: { bg: "var(--dark-card)", fg: "var(--bg-cream)" },
  };
  const c = map[status] || map.draft;
  return <span className="rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ background: c.bg, color: c.fg }}>{status}</span>;
};

export default function Admin() {
  const { user, authLoading } = useApp();
  const [tab, setTab] = useState("overview");

  if (authLoading) return <div className="max-w-7xl mx-auto px-4 py-24 text-center" style={{ color: "var(--text-muted)" }}>Opening the chamber…</div>;
  if (!user || user.role !== "ADMINISTRATOR") return <Navigate to="/login" replace />;

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-[240px_1fr]" style={{ background: "var(--bg-cream)" }}>
      <aside className="hidden lg:block border-r p-6" style={{ background: "var(--obsidian)", borderColor: "var(--dark-card)", color: "var(--bg-cream)" }}>
        <div className="flex items-center gap-2 mb-8">
          <Sparkles className="w-4 h-4" style={{ color: "var(--gold)" }} />
          <div className="eyebrow" style={{ color: "var(--gold-soft)" }}>Temple Admin</div>
        </div>
        <nav className="space-y-1">
          {MENU.map(({ id, label, Icon }) => (
            <button key={id} data-testid={`admin-nav-${id}-btn`} onClick={() => setTab(id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-left"
              style={{ background: tab === id ? "var(--burgundy)" : "transparent", color: tab === id ? "var(--gold)" : "var(--bg-cream)" }}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <div className="hairline my-8" />
        <div className="text-xs opacity-75">
          <div className="font-serif" style={{ color: "var(--gold)" }}>{user.name}</div>
          <div className="mt-1">Administrator</div>
        </div>
      </aside>

      <main className="p-6 sm:p-10 max-w-6xl w-full">
        <div className="lg:hidden mb-6 overflow-x-auto -mx-6 px-6">
          <div className="inline-flex gap-2">
            {MENU.map(({ id, label }) => (
              <button key={id} data-testid={`admin-mobile-nav-${id}-btn`} onClick={() => setTab(id)}
                className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap"
                style={{ background: tab === id ? "var(--burgundy)" : "var(--bg-cream-card)", color: tab === id ? "var(--bg-cream)" : "var(--text-obsidian)", border: `1px solid ${tab === id ? "var(--burgundy)" : "var(--border-cream)"}` }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && <Overview />}
        {tab === "members" && <MembersPanel currentUserId={user.id} />}
        {tab === "teachings" && <ContentPanel coll="teachings" label="Teaching" kinds={["podcast","reflection","audio_teaching","video_teaching","article","music"]} />}
        {tab === "courses" && <LearningPanel />}
        {tab === "library" && <ContentPanel coll="library" label="Resource" types={["Book","Article","Reflection","Study Resource"]} />}
        {tab === "events" && <ContentPanel coll="events" label="Event" />}
        {tab === "attendees" && <AttendeesPanel />}
        {tab === "radio" && <RadioPanel />}
        {tab === "featured" && <FeaturedPanel />}
        {tab === "safety" && <SafetyPanel />}
        {tab === "heart" && <HeartRequestsPanel />}
        {tab === "store" && <StorePanel />}
        {tab === "comms" && <CommsPanel />}
        {tab === "giving" && <GivingPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

// -------------------- Overview --------------------
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const cards = stats ? [
    { label: "Registered members", value: stats.members + stats.administrators, sub: `${stats.active_members} active · ${stats.administrators} admin` },
    { label: "Published courses", value: stats.published_courses, sub: `${stats.draft_courses} in draft` },
    { label: "Published teachings", value: stats.published_teachings, sub: "" },
    { label: "Upcoming gatherings", value: stats.upcoming_events, sub: `${stats.event_registrations} total RSVPs` },
  ] : [];
  const [watchers, setWatchers] = useState(null);
  useEffect(() => { api.get("/admin/watchers").then((r) => setWatchers(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <div className="mb-8">
        <div className="eyebrow mb-1">Admin · Overview</div>
        <h1 className="font-serif text-3xl sm:text-4xl" style={{ color: "var(--burgundy)" }}>The Temple, at a glance.</h1>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} data-testid={`admin-stat-${i}`} className="rounded-2xl border p-5" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
            <div className="eyebrow mb-1">{c.label}</div>
            <div className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>{c.value}</div>
            {c.sub && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{c.sub}</div>}
          </div>
        ))}
      </div>
      {watchers && (
        <div className="rounded-2xl border p-5 mb-8 flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold)" }} data-testid="admin-watchers-indicator">
          <div><div className="eyebrow mb-1">Watchers Early Access</div><div className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>{watchers.claimed_count} / {watchers.limit} claimed</div></div>
          <div className="text-xs" style={{ color: watchers.preview_url ? "var(--text-muted)" : "var(--rose-dark)" }}>{watchers.preview_url ? "Preview link configured (private)" : "Preview link not set — Settings → Watchers preview URL"}</div>
        </div>
      )}
      <div className="rounded-2xl border p-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <div className="eyebrow mb-3">Recent activity</div>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Activity tracking will appear here once event streams are enabled.</p>
      </div>
    </div>
  );
}

// -------------------- Members --------------------
function MembersPanel({ currentUserId }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [related, setRelated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/members${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setRows(data || []);
    setChecked((prev) => new Set([...prev].filter((id) => (data || []).some((r) => r.id === id))));
  }, [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!selected) { setRelated(null); return; }
    api.get(`/admin/members/${selected.id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated(null));
  }, [selected]);

  const save = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/members/${id}`, patch);
      toast("Member updated");
      setSelected(data.user);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const selectable = rows.filter((r) => r.id !== currentUserId);
  const allChecked = selectable.length > 0 && selectable.every((r) => checked.has(r.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(selectable.map((r) => r.id)));
  const toggleOne = (id) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const count = checked.size;

  const bulk = async (action) => {
    const label = { deactivate: "Deactivate", reactivate: "Reactivate", delete: "PERMANENTLY DELETE", anonymize: "Anonymize & detach" }[action];
    const msg = action === "delete"
      ? `${label} ${count} account${count === 1 ? "" : "s"}?\n\nThis removes the login and personal data (journal, progress, saved items). Accounts with protected history (purchases, giving, membership, Heart Requests, safety, registrations, consent) will be skipped and reported. This cannot be undone.`
      : action === "anonymize"
        ? `${label} ${count} account${count === 1 ? "" : "s"}?\n\nLogins and personal data are removed; financial, safety, audit, order and consent history is preserved under an anonymized stub. This cannot be undone.`
        : `${label} ${count} account${count === 1 ? "" : "s"}?`;
    if (action !== "reactivate" && !window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/members/bulk", { ids: [...checked], action });
      const failed = data.results.filter((r) => !r.ok);
      toast(`${data.succeeded} ${action}d · ${data.failed} skipped`);
      if (failed.length) toast.error(failed.map((f) => `${rows.find((r) => r.id === f.id)?.name || f.id}: ${f.error}`).join("\n"), { duration: 12000 });
      setChecked(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
    finally { setBusy(false); }
  };

  const removeOne = async (mode) => {
    const u = selected;
    const msg = mode === "delete"
      ? `PERMANENTLY DELETE ${u.name} (${u.email})?\n\nThis is not the same as Deactivate. The login and personal data are removed and cannot be recovered.`
      : `Anonymize & detach ${u.name} (${u.email})?\n\nThe login and personal data are removed; protected history is preserved under an anonymized stub. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/members/${u.id}?mode=${mode}`);
      toast(data.result === "deleted" ? "Account deleted" : "Account anonymized and detached");
      setSelected(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed", { duration: 12000 }); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
        <div>
          <div className="eyebrow mb-1">Admin · Members</div>
          <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Community roster.</h1>
        </div>
        <div className="flex items-center gap-2 rounded-full border px-3 py-1.5" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
          <Search className="w-3.5 h-3.5" />
          <input data-testid="member-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="text-sm bg-transparent outline-none" />
        </div>
      </div>

      {count > 0 && (
        <div className="mb-4 rounded-2xl border p-3 flex flex-wrap items-center gap-2" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold)" }} data-testid="members-bulk-bar">
          <span className="text-sm font-medium mr-2" style={{ color: "var(--burgundy)" }} data-testid="members-selected-count">{count} account{count === 1 ? "" : "s"} selected</span>
          <button data-testid="bulk-deactivate-btn" disabled={busy} onClick={() => bulk("deactivate")} className="rounded-full px-3 py-1.5 text-xs font-medium border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Deactivate Selected</button>
          <button data-testid="bulk-reactivate-btn" disabled={busy} onClick={() => bulk("reactivate")} className="rounded-full px-3 py-1.5 text-xs font-medium border" style={{ borderColor: "var(--teal)", color: "var(--teal-deep)" }}>Reactivate Selected</button>
          <button data-testid="bulk-anonymize-btn" disabled={busy} onClick={() => bulk("anonymize")} className="rounded-full px-3 py-1.5 text-xs font-medium border" style={{ borderColor: "var(--rose-dark)", color: "var(--rose-dark)" }}>Anonymize Selected</button>
          <button data-testid="bulk-delete-btn" disabled={busy} onClick={() => bulk("delete")} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: "var(--rose-dark)", color: "var(--bg-cream)" }}>Delete Selected</button>
          <button data-testid="bulk-clear-btn" onClick={() => setChecked(new Set())} className="ml-auto text-xs ink-link" style={{ color: "var(--text-muted)" }}>Clear</button>
        </div>
      )}

      <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg-cream)" }}>
              <tr className="text-left">
                <th className="px-4 py-3"><input type="checkbox" data-testid="members-select-all-checkbox" checked={allChecked} onChange={toggleAll} disabled={selectable.length === 0} title="Select all shown" /></th>
                {["Name","Email","Role","Stage","Joined","Status"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>{h}</th>
                ))}
                <th className="px-4 py-3"/>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center" style={{ color: "var(--text-muted)" }}>No members match your search.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`member-row-${r.id}`} className="border-t" style={{ borderColor: "var(--border-cream)", opacity: r.anonymized_at ? 0.6 : 1 }}>
                  <td className="px-4 py-3">
                    {r.id === currentUserId
                      ? <input type="checkbox" disabled title="You cannot select your own account" data-testid={`member-select-${r.id}-checkbox`} />
                      : <input type="checkbox" data-testid={`member-select-${r.id}-checkbox`} checked={checked.has(r.id)} onChange={() => toggleOne(r.id)} />}
                  </td>
                  <td className="px-4 py-3 font-serif" style={{ color: "var(--burgundy)" }}>{r.name}{r.id === currentUserId ? " (you)" : ""}</td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3">{r.role}</td>
                  <td className="px-4 py-3 font-mono">{String(r.current_stage || 1).padStart(2,"0")} · {STAGE_NAMES[(r.current_stage || 1) - 1]}</td>
                  <td className="px-4 py-3">{(r.created_at || "").slice(0,10)}</td>
                  <td className="px-4 py-3" data-testid={`member-status-${r.id}`}>{r.anonymized_at ? "Anonymized" : r.is_active === false ? "Deactivated" : "Active"}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`member-edit-${r.id}-btn`} onClick={() => setSelected(r)} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Manage</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected?.name || "Member"}>
        {selected && (
          <div>
            <div className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>{selected.email} · Joined {(selected.created_at || "").slice(0,10)}</div>
            <Field label="Role">
              <select data-testid="member-role-select" defaultValue={selected.role}
                onChange={(e) => {
                  if (selected.id === currentUserId && e.target.value !== "ADMINISTRATOR") {
                    toast.error("You cannot remove your own administrator role.");
                    e.target.value = selected.role;
                    return;
                  }
                  if (!window.confirm(`Change role to ${e.target.value}?`)) { e.target.value = selected.role; return; }
                  save(selected.id, { role: e.target.value });
                }}
                className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
                <option value="MEMBER">MEMBER</option>
                <option value="ADMINISTRATOR">ADMINISTRATOR</option>
              </select>
            </Field>
            <Field label="Current journey stage">
              <select data-testid="member-stage-select" defaultValue={selected.current_stage || 1}
                onChange={(e) => save(selected.id, { current_stage: Number(e.target.value) })}
                className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
                {STAGE_NAMES.map((n, i) => <option key={n} value={i+1}>{String(i+1).padStart(2,"0")} · {n}</option>)}
              </select>
            </Field>
            <Field label="Account status">
              {selected.id === currentUserId ? (
                <p className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="member-self-protected-note">This is your own account. You cannot deactivate or delete the account you are signed in with.</p>
              ) : (
                <button data-testid="member-active-toggle-btn"
                  onClick={() => {
                    if (!window.confirm(selected.is_active === false ? "Reactivate this account?" : "Deactivate this account? The member keeps all data and can be reactivated later.")) return;
                    save(selected.id, { is_active: !(selected.is_active !== false) });
                  }}
                  disabled={!!selected.anonymized_at}
                  className="rounded-full px-4 py-2 text-sm font-medium border disabled:opacity-50"
                  style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
                  {selected.is_active === false ? "Reactivate account" : "Deactivate account"}
                </button>
              )}
            </Field>

            {selected.id !== currentUserId && !selected.anonymized_at && (
              <div className="mt-6 rounded-xl border p-4" style={{ borderColor: "var(--rose)" }} data-testid="member-danger-zone">
                <div className="eyebrow mb-2" style={{ color: "var(--rose-dark)" }}>Remove account · irreversible</div>
                {!related ? <div className="text-xs" style={{ color: "var(--text-muted)" }}>Checking related records…</div> : (
                  <>
                    {related.protected.length > 0 ? (
                      <div className="text-xs mb-3" style={{ color: "var(--text-obsidian)" }} data-testid="member-protected-records">
                        <strong>Deletion unavailable.</strong> This account has protected history that must be preserved:
                        <ul className="list-disc ml-4 mt-1">{related.protected.map((p) => <li key={p.label}>{p.label} · {p.count}</li>)}</ul>
                        <p className="mt-2">You can <strong>Anonymize &amp; detach</strong> instead: the login, name, email and personal data are removed; the protected records remain under an anonymized stub.</p>
                      </div>
                    ) : (
                      <div className="text-xs mb-3" style={{ color: "var(--text-obsidian)" }} data-testid="member-safe-delete-note">No protected records. Deleting removes the login{related.personal.length ? ` and personal data (${related.personal.map((p) => `${p.label} · ${p.count}`).join(", ")})` : ""}.</div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <button data-testid="member-delete-btn" disabled={busy || !related.deletable} onClick={() => removeOne("delete")} className="rounded-full px-4 py-2 text-xs font-medium disabled:opacity-40" style={{ background: "var(--rose-dark)", color: "var(--bg-cream)" }}>Delete permanently</button>
                      <button data-testid="member-anonymize-btn" disabled={busy} onClick={() => removeOne("anonymize")} className="rounded-full px-4 py-2 text-xs font-medium border" style={{ borderColor: "var(--rose-dark)", color: "var(--rose-dark)" }}>Anonymize &amp; detach</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

// -------------------- Content editor generic --------------------
function ContentPanel({ coll, label, kinds, types }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/content/${coll}${filter !== "all" ? `?status=${filter}` : ""}`);
    setRows(data || []);
  }, [coll, filter]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({
    __new: true,
    status: "draft",
    ...(coll === "teachings" ? { kind: "podcast", title: "", host: "" } : {}),
    ...(coll === "library" ? { type: "Article", title: "", author: "Yisra'el Apriale", availability: "available" } : {}),
    ...(coll === "events" ? { format: "Virtual", title: "", date: "" } : {}),
  });

  const save = async (doc) => {
    try {
      if (doc.__new) {
        const body = { ...doc }; delete body.__new;
        const { data } = await api.post(`/admin/content/${coll}`, body);
        toast(`${label} created${coll === "library" || coll === "events" ? " — you can now upload an image" : ""}`);
        if (coll === "library" || coll === "events") { setEditing(data); load(); return; }
      } else {
        const { id, __new, ...body } = doc;
        await api.patch(`/admin/content/${coll}/${id}`, body);
        toast(`${label} saved`);
      }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${label.toLowerCase()}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/content/${coll}/${id}`); toast(`${label} removed`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
        <div>
          <div className="eyebrow mb-1">Admin · {label}s</div>
          <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Manage {label.toLowerCase()}s.</h1>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid={`${coll}-filter-select`} value={filter} onChange={(e) => setFilter(e.target.value)}
            className="rounded-full border px-3 py-1.5 text-xs bg-transparent" style={{ borderColor: "var(--border-cream)" }}>
            <option value="all">All</option><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
          </select>
          <button data-testid={`${coll}-new-btn`} onClick={openNew}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
            style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
            <Plus className="w-4 h-4" /> New {label.toLowerCase()}
          </button>
          {coll === "library" && <AmazonImportButton onDone={load} />}
        </div>
      </div>

      <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg-cream)" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>Title</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>Type</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>Status</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>Updated</th>
                <th className="px-4 py-3"/>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (<tr><td colSpan={5} className="px-4 py-10 text-center" style={{ color: "var(--text-muted)" }}>No {label.toLowerCase()}s yet.</td></tr>)}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`${coll}-row-${r.id}`} className="border-t" style={{ borderColor: "var(--border-cream)" }}>
                  <td className="px-4 py-3 font-serif" style={{ color: "var(--burgundy)" }}>{r.title}</td>
                  <td className="px-4 py-3">{r.kind || r.type || r.format || "—"}{r.type === "Book" && r.availability === "upcoming" ? " · upcoming" : ""}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status || "draft"} /></td>
                  <td className="px-4 py-3">{(r.updated_at || r.created_at || "").slice(0,10)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {coll === "library" && r.type !== "Book" && r.body && <a data-testid={`${coll}-preview-${r.id}-link`} href={`/read/${r.id}?preview=1`} target="_blank" rel="noreferrer" className="text-xs ink-link mr-3" style={{ color: "var(--burgundy)" }}>Preview</a>}
                    {coll === "library" && r.status === "published" && <button data-testid={`${coll}-unpublish-${r.id}-btn`} onClick={() => save({ ...r, status: "draft" })} className="text-xs ink-link mr-3" style={{ color: "var(--burgundy)" }}>Unpublish</button>}
                    <button data-testid={`${coll}-edit-${r.id}-btn`} onClick={() => setEditing(r)} className="text-xs ink-link mr-3" style={{ color: "var(--burgundy)" }}>Edit</button>
                    <button data-testid={`${coll}-delete-${r.id}-btn`} onClick={() => remove(r.id)} className="text-xs ink-link" style={{ color: "var(--rose-dark)" }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing?.__new ? `New ${label}` : `Edit ${label}`}>
        {editing && (
          <ContentForm coll={coll} kinds={kinds} types={types} doc={editing} onChange={setEditing} onSave={save} />
        )}
      </Drawer>
    </div>
  );
}

function ContentForm({ coll, kinds, types, doc, onChange, onSave }) {
  const set = (k, v) => onChange({ ...doc, [k]: v });
  return (
    <div>
      {coll === "teachings" && (
        <Field label="Type"><select data-testid="teaching-kind-select" value={doc.kind || "podcast"} onChange={(e) => set("kind", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select></Field>
      )}
      {coll === "library" && (
        <Field label="Type"><select data-testid="library-type-select" value={doc.type || "Article"} onChange={(e) => set("type", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
          {types.map((k) => <option key={k} value={k}>{k}</option>)}
        </select></Field>
      )}
      {coll === "events" && (
        <Field label="Format"><select data-testid="event-format-select" value={doc.format || "Virtual"} onChange={(e) => set("format", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
          <option>Virtual</option><option>In-person</option><option>Hybrid</option>
        </select></Field>
      )}
      <Field label="Title"><input data-testid="content-title-input" value={doc.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      {(coll === "teachings" || coll === "courses" || coll === "library") && (
        <Field label="Subtitle / Tagline"><input value={doc.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      )}
      {coll === "teachings" && (
        <>
          <Field label="Author / Speaker"><input data-testid="teaching-host-input" value={doc.host || ""} onChange={(e) => set("host", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Episode label"><input value={doc.episode || ""} onChange={(e) => set("episode", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Duration"><input value={doc.duration || ""} onChange={(e) => set("duration", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Publication date"><input value={doc.released_at || ""} onChange={(e) => set("released_at", e.target.value)} placeholder="e.g. Feb 6, 2026" className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Audio URL"><input value={doc.audio_url || ""} onChange={(e) => set("audio_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Video URL"><input value={doc.video_url || ""} onChange={(e) => set("video_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        </>
      )}
      {coll === "library" && (
        <>
          <Field label="Author"><input value={doc.author || ""} onChange={(e) => set("author", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label={doc.type === "Book" ? "Format / length" : "Length / read time"}><input value={doc.length || ""} onChange={(e) => set("length", e.target.value)} placeholder="e.g. 12 min read" className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          {doc.type === "Book" && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Availability"><select data-testid="library-availability-select" value={doc.availability || "available"} onChange={(e) => set("availability", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
                <option value="available">Available</option><option value="upcoming">Upcoming</option>
              </select></Field>
              <Field label="Release date (optional)"><div className="flex gap-2"><input data-testid="library-release-date-input" type="date" value={doc.release_date || ""} onChange={(e) => set("release_date", e.target.value || null)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />{doc.release_date && <button type="button" data-testid="library-release-date-clear-btn" onClick={() => set("release_date", null)} className="text-xs whitespace-nowrap ink-link" style={{ color: "var(--burgundy)" }}>Clear</button>}</div></Field>
            </div>
          )}
          <Field label={doc.type === "Book" ? "External purchase/read URL (e.g. Amazon product page — not required for Upcoming)" : "External read/purchase URL (optional)"}><input data-testid="library-external-url-input" value={doc.external_url || ""} onChange={(e) => set("external_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          {doc.type === "Book" && <Field label="Paperback URL (optional)"><input value={doc.paperback_url || ""} onChange={(e) => set("paperback_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>}
          <Field label="Excerpt"><textarea data-testid="library-excerpt-input" rows={2} value={doc.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        </>
      )}
      {coll === "events" && (
        <>
          <Field label="Date (display)"><input data-testid="event-date-input" value={doc.date || ""} onChange={(e) => set("date", e.target.value)} placeholder="e.g. Feb 27, 2026" className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Calendar date (used to hide past events)"><input data-testid="event-date-iso-input" type="date" value={doc.date_iso || ""} onChange={(e) => set("date_iso", e.target.value || null)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Start"><input value={doc.start_time || ""} onChange={(e) => set("start_time", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <Field label="End"><input value={doc.end_time || ""} onChange={(e) => set("end_time", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <Field label="Timezone"><input value={doc.time_zone || ""} onChange={(e) => set("time_zone", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          </div>
          <Field label="Location"><input value={doc.location || ""} onChange={(e) => set("location", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Virtual meeting URL (private — shown only to confirmed attendees)"><input value={doc.virtual_url || ""} onChange={(e) => set("virtual_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Registration mode"><select data-testid="event-registration-mode-select" value={doc.registration_mode || "native"} onChange={(e) => set("registration_mode", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
            <option value="native">Native Temple registration</option><option value="external">External registration (Planning Center, etc.)</option><option value="none">Registration not required</option>
          </select></Field>
          {doc.registration_mode === "external" && <Field label="External registration URL"><input data-testid="event-registration-url-input" value={doc.registration_url || ""} onChange={(e) => set("registration_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>}
          <Field label="Capacity (actual registration limit for native registration)"><input data-testid="event-capacity-input" type="number" value={doc.capacity || ""} onChange={(e) => set("capacity", e.target.value ? Number(e.target.value) : null)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Seats note (display)"><input value={doc.seats || ""} onChange={(e) => set("seats", e.target.value)} placeholder='e.g. "12 open"' className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        </>
      )}
      <Field label="Description"><textarea data-testid="content-description-input" rows={3} value={doc.description || ""} onChange={(e) => set("description", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      {coll === "library" ? <BodyEditor value={doc.body || ""} onChange={(v) => set("body", v)} /> : coll !== "events" && (
        <Field label="Full body"><textarea rows={5} value={doc.body || ""} onChange={(e) => set("body", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      )}
      {coll === "library" || coll === "events" ? <LibraryImageField doc={doc} set={set} endpoint={coll === "events" ? "/admin/events" : "/admin/library"} /> : <Field label="Cover image URL"><input value={doc.art_url || ""} onChange={(e) => set("art_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>}
      {coll !== "events" && (
        <Field label="Journey stage (optional)"><select value={doc.stage || ""} onChange={(e) => set("stage", e.target.value ? Number(e.target.value) : null)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
          <option value="">— none —</option>
          {STAGE_NAMES.map((n, i) => <option key={n} value={i+1}>{String(i+1).padStart(2,"0")} · {n}</option>)}
        </select></Field>
      )}
      <label className="inline-flex items-center gap-2 text-sm mb-4">
        <input type="checkbox" data-testid="content-featured-checkbox" checked={!!doc.featured} onChange={(e) => set("featured", e.target.checked)} />
        Featured
      </label>
      <Field label="Status"><select data-testid="content-status-select" value={doc.status || "draft"} onChange={(e) => set("status", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
        <option value="draft">Draft</option><option value="published">Published</option>{coll === "library" && <option value="scheduled">Scheduled</option>}<option value="archived">Archived</option>
      </select></Field>
      {coll === "library" && doc.status === "scheduled" && (
        <Field label="Publish at (your local time — stays private until then)"><input data-testid="library-publish-at-input" type="datetime-local" value={doc.publish_at ? new Date(doc.publish_at).toISOString().slice(0, 16) : ""} onChange={(e) => set("publish_at", e.target.value ? new Date(e.target.value).toISOString() : null)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      )}
      <div className="flex items-center gap-3 mt-6">
        <button data-testid="content-save-btn" onClick={() => onSave(doc)} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save</button>
        {!doc.__new && doc.status !== "published" && (
          <button data-testid="content-publish-btn" onClick={() => onSave({ ...doc, status: "published" })} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Save & publish</button>
        )}
      </div>
    </div>
  );
}

// -------------------- Library helpers (Markdown body, image upload, Amazon import) --------------------
function BodyEditor({ value, onChange }) {
  const [preview, setPreview] = useState(false);
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className={labelCls} style={{ color: "var(--gold-dark)" }}>Full body (Markdown)</span>
        <button type="button" data-testid="library-body-preview-toggle" onClick={() => setPreview(!preview)} className="text-[11px] ink-link" style={{ color: "var(--burgundy)" }}>{preview ? "Edit" : "Preview"}</button>
      </div>
      {preview ? (
        <div className="rounded-lg border p-4 min-h-[120px]" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}><Markdown style={{ color: "var(--text-obsidian)" }}>{value}</Markdown></div>
      ) : (
        <textarea data-testid="library-body-input" rows={14} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls + " font-mono text-[13px] leading-relaxed"} style={{ borderColor: "var(--border-cream)" }} />
      )}
      <span className="block text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Blank line between paragraphs · # Heading · ## Subheading · **bold** · *italic* · &gt; quotation · - bullet · 1. numbered · [text](https://…). Paste from any editor. Articles with a body open as full pages on the Temple site.</span>
    </div>
  );
}

function LibraryImageField({ doc, set, endpoint = "/admin/library" }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`${endpoint}/${doc.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("art_url", data.art_url); toast("Image uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const remove = async () => { if (doc.id && !doc.__new) await api.delete(`${endpoint}/${doc.id}/image`); set("art_url", ""); };
  return (
    <Field label="Cover / feature image">
      <div className="flex items-center gap-3">
        {doc.art_url ? <img src={assetUrl(doc.art_url)} alt="" data-testid="library-image-preview" className="w-16 h-20 rounded-lg object-cover flex-shrink-0" /> : <div className="w-16 h-20 rounded-lg flex-shrink-0" style={{ background: "var(--bg-cream-card)" }} />}
        <input value={doc.art_url || ""} onChange={(e) => set("art_url", e.target.value)} placeholder="Paste an image URL, or upload →" className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
        {doc.id && !doc.__new && (
          <label className="inline-flex items-center text-xs rounded-full px-3 py-2 border cursor-pointer whitespace-nowrap" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Uploading…" : "Upload"}<input data-testid="library-image-upload-input" type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" disabled={busy} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          </label>
        )}
        {doc.art_url && <button type="button" data-testid="library-image-remove-btn" onClick={remove} className="text-xs ink-link" style={{ color: "var(--rose-dark)" }}>Remove</button>}
      </div>
      {doc.__new && <span className="block text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Save first to upload an image file; a URL can be used right away.</span>}
    </Field>
  );
}

function AmazonImportButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Import books from the configured Amazon Author page? New titles are added as DRAFT Books; existing Temple books are never changed or removed.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/library/amazon/import", {});
      toast(`Amazon: ${data.found} titles found · ${data.created.length} imported as drafts · ${data.skipped_existing.length} already in Library`, { duration: 8000 });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Amazon import failed. Existing books are untouched."); } finally { setBusy(false); }
  };
  return (
    <button data-testid="library-amazon-import-btn" onClick={run} disabled={busy} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border disabled:opacity-60" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
      {busy ? "Importing…" : "Import Amazon books"}
    </button>
  );
}

// -------------------- Courses (list + lessons) --------------------
function CoursesPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [managingLessons, setManagingLessons] = useState(null);

  const load = async () => setRows((await api.get("/admin/content/courses")).data || []);
  useEffect(() => { load(); }, []);

  const save = async (doc) => {
    try {
      if (doc.__new) {
        const b = { ...doc }; delete b.__new;
        await api.post("/admin/content/courses", b); toast("Course created");
      } else {
        const { id, __new, ...b } = doc;
        await api.patch(`/admin/content/courses/${id}`, b); toast("Course saved");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this course and all its lessons? Member progress records will remain but will no longer reference a course.")) return;
    await api.delete(`/admin/content/courses/${id}`); toast("Course removed"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-1">Admin · Courses</div>
          <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Learning Circle courses.</h1>
        </div>
        <button data-testid="course-new-btn" onClick={() => setEditing({ __new: true, status: "draft", stage: 1, teacher: "", title: "" })}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
          style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
          <Plus className="w-4 h-4" /> New course
        </button>
      </div>

      <div className="grid gap-3">
        {rows.length === 0 && <div className="rounded-2xl border p-8 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }}>No courses yet.</div>}
        {rows.map((c) => (
          <div key={c.id} data-testid={`course-row-${c.id}`} className="rounded-2xl border p-4 flex items-center gap-4"
            style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
            <div className="w-16 h-16 rounded-lg flex items-center justify-center font-mono text-xs" style={{ background: "var(--burgundy)", color: "var(--gold)" }}>
              {String(c.stage).padStart(2, "0")}<br/>{STAGE_NAMES[c.stage - 1]?.slice(0, 4)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg leading-tight" style={{ color: "var(--burgundy)" }}>{c.title}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{c.teacher || "—"} · {c.lesson_count} lesson{c.lesson_count === 1 ? "" : "s"}</div>
            </div>
            <StatusBadge status={c.status || "draft"} />
            <button data-testid={`course-lessons-${c.id}-btn`} onClick={() => setManagingLessons(c)} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Lessons</button>
            <button data-testid={`course-edit-${c.id}-btn`} onClick={() => setEditing(c)} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Edit</button>
            <button data-testid={`course-delete-${c.id}-btn`} onClick={() => remove(c.id)} className="text-xs ink-link" style={{ color: "var(--rose-dark)" }}>Delete</button>
          </div>
        ))}
      </div>

      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing?.__new ? "New course" : "Edit course"}>
        {editing && (
          <div>
            <Field label="Title"><input data-testid="course-title-input" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <Field label="Subtitle / Tagline"><input value={editing.tagline || editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value, tagline: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <Field label="Instructor"><input data-testid="course-teacher-input" value={editing.teacher || ""} onChange={(e) => setEditing({ ...editing, teacher: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <Field label="Journey stage"><select data-testid="course-stage-select" value={editing.stage || 1} onChange={(e) => setEditing({ ...editing, stage: Number(e.target.value) })} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
              {STAGE_NAMES.map((n, i) => <option key={n} value={i+1}>{String(i+1).padStart(2,"0")} · {n}</option>)}
            </select></Field>
            <Field label="Estimated duration"><input value={editing.duration || ""} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <Field label="Description"><textarea rows={4} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <Field label="Cover image URL"><input value={editing.art_url || ""} onChange={(e) => setEditing({ ...editing, art_url: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <label className="inline-flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />Featured</label>
            <Field label="Status"><select data-testid="course-status-select" value={editing.status || "draft"} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
              <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
            </select></Field>
            <button data-testid="course-save-btn" onClick={() => save(editing)} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium mt-2" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save</button>
          </div>
        )}
      </Drawer>

      <Drawer open={!!managingLessons} onClose={() => { setManagingLessons(null); load(); }} title={`Lessons — ${managingLessons?.title || ""}`}>
        {managingLessons && <LessonsEditor course={managingLessons} />}
      </Drawer>
    </div>
  );
}

function LessonsEditor({ course }) {
  const [lessons, setLessons] = useState([]);
  const [creating, setCreating] = useState({ title: "" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/courses/${course.id}/lessons`);
    setLessons(data || []);
  }, [course.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!creating.title.trim()) return;
    await api.post(`/admin/courses/${course.id}/lessons`, creating);
    setCreating({ title: "" });
    toast("Lesson added"); load();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= lessons.length) return;
    const order = [...lessons];
    [order[i], order[j]] = [order[j], order[i]];
    await api.post(`/admin/courses/${course.id}/lessons/reorder`, { order: order.map((l) => l.id) });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    await api.delete(`/admin/lessons/${id}`); toast("Lesson removed"); load();
  };

  const saveEdit = async () => {
    const { id, ...body } = editing;
    await api.patch(`/admin/lessons/${id}`, body);
    toast("Lesson saved"); setEditing(null); load();
  };

  return (
    <div>
      <div className="mb-6 rounded-lg border p-3" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <input data-testid="lesson-new-title-input" value={creating.title} onChange={(e) => setCreating({ ...creating, title: e.target.value })} placeholder="New lesson title" className={inputCls + " mb-2"} style={{ borderColor: "var(--border-cream)" }} />
        <button data-testid="lesson-add-btn" onClick={add} className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
          <Plus className="w-3.5 h-3.5" /> Add lesson
        </button>
      </div>
      {lessons.length === 0 && <div className="text-sm text-center py-6" style={{ color: "var(--text-muted)" }}>No lessons yet.</div>}
      <div className="space-y-2">
        {lessons.map((l, i) => (
          <div key={l.id} data-testid={`lesson-row-${l.id}`} className="rounded-lg border p-3 flex items-center gap-3" style={{ borderColor: "var(--border-cream)" }}>
            <div className="font-mono text-xs" style={{ color: "var(--gold-dark)" }}>{String(i+1).padStart(2,"0")}</div>
            <div className="flex-1 font-serif" style={{ color: "var(--burgundy)" }}>{l.title}</div>
            <button data-testid={`lesson-up-${l.id}-btn`} onClick={() => move(i, -1)} className="p-1"><ChevronUp className="w-4 h-4" /></button>
            <button data-testid={`lesson-down-${l.id}-btn`} onClick={() => move(i, 1)} className="p-1"><ChevronDown className="w-4 h-4" /></button>
            <button data-testid={`lesson-edit-${l.id}-btn`} onClick={() => setEditing(l)} className="p-1"><Edit3 className="w-4 h-4" /></button>
            <button data-testid={`lesson-delete-${l.id}-btn`} onClick={() => remove(l.id)} className="p-1"><Trash2 className="w-4 h-4" style={{ color: "var(--rose-dark)" }} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="mt-6 rounded-lg border p-4" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold-soft)" }}>
          <div className="eyebrow mb-3">Editing lesson · {editing.title}</div>
          <Field label="Title"><input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Written content"><textarea rows={4} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Audio URL"><input value={editing.audio_url || ""} onChange={(e) => setEditing({ ...editing, audio_url: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Video URL"><input value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Journaling prompt"><textarea rows={2} value={editing.prompt || ""} onChange={(e) => setEditing({ ...editing, prompt: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Practice / integration prompt"><textarea rows={2} value={editing.practice_prompt || ""} onChange={(e) => setEditing({ ...editing, practice_prompt: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <Field label="Estimated duration (min)"><input type="number" value={editing.duration_min || ""} onChange={(e) => setEditing({ ...editing, duration_min: e.target.value ? Number(e.target.value) : null })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
          <div className="flex gap-2">
            <button data-testid="lesson-save-btn" onClick={saveEdit} className="rounded-full px-4 py-2 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save lesson</button>
            <button onClick={() => setEditing(null)} className="text-xs ink-link" style={{ color: "var(--text-muted)" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Attendees --------------------
function AttendeesPanel() {
  const [events, setEvents] = useState([]);
  const [sel, setSel] = useState(null);
  const [data, setData] = useState({ count: 0, attendees: [], totals: {} });
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/admin/content/events").then((r) => setEvents(r.data || [])); }, []);
  const load = async (ev) => { const { data } = await api.get(`/admin/events/${ev.id}/attendees`); setData(data); };
  const open = async (ev) => { setSel(ev); setFilter("all"); await load(ev); };
  const act = async (a, action) => {
    try { await api.post(`/admin/events/${sel.id}/attendees/${a.id}/${action}`); toast(`Registration ${action === "cancel" ? "cancelled" : action === "notified" ? "marked notified" : "confirmed"}`); load(sel); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const exportCsv = async () => {
    const res = await api.get(`/admin/events/${sel.id}/attendees.csv`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${sel.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-attendees.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const t = data.totals || {};
  const rows = data.attendees.filter((a) => filter === "all" || a.status === filter);
  const th = "px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-left";
  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-1">Admin · Attendees</div>
        <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Event registrations.</h1>
      </div>
      <div className="grid gap-3">
        {events.length === 0 && <div className="rounded-2xl border p-6 text-sm text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)", color: "var(--text-muted)" }}>No events yet.</div>}
        {events.map((e) => (
          <div key={e.id} data-testid={`attendees-event-${e.id}`} className="rounded-2xl border p-4 flex items-center gap-4" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>{e.title}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{e.date} · {e.format} · {e.registration_mode || "native"}{e.capacity ? ` · capacity ${e.capacity}` : ""}</div>
            </div>
            <button data-testid={`attendees-view-${e.id}-btn`} onClick={() => open(e)} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>View attendees</button>
          </div>
        ))}
      </div>
      <Drawer open={!!sel} onClose={() => setSel(null)} title={sel?.title || ""}>
        <div className="grid grid-cols-5 gap-2 mb-4 text-center" data-testid="attendees-totals">
          {[["Confirmed", t.confirmed], ["Waitlisted", t.waitlisted], ["Cancelled", t.cancelled], ["Capacity", t.capacity ?? "—"], ["Remaining", t.remaining ?? "—"]].map(([k, v]) => (
            <div key={k} className="rounded-lg border p-2" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
              <div className="font-serif text-xl" style={{ color: "var(--burgundy)" }} data-testid={`attendees-total-${k.toLowerCase()}`}>{v ?? 0}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>{k}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {["all", "confirmed", "waitlisted", "cancelled"].map((f) => <button key={f} onClick={() => setFilter(f)} className="rounded-full px-3 py-1 text-xs border" style={{ background: filter === f ? "var(--burgundy)" : "transparent", color: filter === f ? "var(--bg-cream)" : "var(--text-obsidian)", borderColor: "var(--border-cream)" }}>{f}</button>)}
          <span className="flex-1" />
          <button data-testid="attendees-export-csv-btn" onClick={exportCsv} className="text-xs rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Export CSV</button>
        </div>
        {rows.length === 0 ? <div className="text-sm" style={{ color: "var(--text-muted)" }}>No registrations here.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-cream-card)", color: "var(--gold-dark)" }}><tr><th className={th}>Name</th><th className={th}>Email</th><th className={th}>Status</th><th className={th}>Type</th><th className={th}>Registered</th><th className={th}>Consent</th><th className={th}>Notes</th><th className={th}></th></tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} data-testid={`attendee-row-${a.id}`} className="border-t align-top" style={{ borderColor: "var(--border-cream)" }}>
                    <td className="px-3 py-2 font-serif" style={{ color: "var(--burgundy)" }}>{a.first_name} {a.last_name}{a.phone ? <div className="text-[11px] font-sans" style={{ color: "var(--text-muted)" }}>{a.phone}</div> : null}</td>
                    <td className="px-3 py-2 break-all">{a.email}</td>
                    <td className="px-3 py-2"><span className="font-mono text-[10px] uppercase" data-testid={`attendee-status-${a.id}`}>{a.status}{a.status === "waitlisted" && a.waitlist_position ? ` #${a.waitlist_position}` : ""}</span>{a.promoted_at && <div className="text-[10px]" style={{ color: a.notified ? "var(--text-muted)" : "var(--rose-dark)" }}>promoted{a.notified ? " · notified" : " · needs notice"}</div>}</td>
                    <td className="px-3 py-2 text-xs">{a.is_member ? "member" : "guest"}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{(a.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-xs">{a.marketing_consent ? "yes" : "no"}</td>
                    <td className="px-3 py-2 text-xs max-w-[160px]">{a.accommodation}{a.note ? <div>{a.note}</div> : null}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {a.status !== "cancelled" && <button data-testid={`attendee-cancel-${a.id}-btn`} onClick={() => act(a, "cancel")} className="ink-link mr-2" style={{ color: "var(--rose-dark)" }}>Cancel</button>}
                      {a.status !== "confirmed" && <button data-testid={`attendee-confirm-${a.id}-btn`} onClick={() => act(a, "confirm")} className="ink-link mr-2" style={{ color: "var(--burgundy)" }}>{a.status === "waitlisted" ? "Move to confirmed" : "Restore"}</button>}
                      {a.promoted_at && !a.notified && <button onClick={() => act(a, "notified")} className="ink-link" style={{ color: "var(--teal)" }}>Mark notified</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Drawer>
    </div>
  );
}

// -------------------- Safety Alerts --------------------
function SafetyPanel() {
  const [alerts, setAlerts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [audit, setAudit] = useState([]);
  const load = async () => {
    const q = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    const { data } = await api.get(`/admin/safety/alerts${q}`);
    setAlerts(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  const openAlert = async (a) => {
    const { data } = await api.get(`/admin/safety/alerts/${a.id}`);
    setOpen(data.flag); setAudit(data.audit || []);
  };
  const changeStatus = async (status) => {
    const { data } = await api.patch(`/admin/safety/alerts/${open.id}`, { status });
    setOpen(data); toast("Status updated"); load();
    const a = await api.get(`/admin/safety/alerts/${open.id}`); setAudit(a.data.audit);
  };
  const addNote = async (notes) => {
    const { data } = await api.patch(`/admin/safety/alerts/${open.id}`, { notes });
    setOpen(data); toast("Note recorded");
    const a = await api.get(`/admin/safety/alerts/${open.id}`); setAudit(a.data.audit);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">Admin · Safety Alerts</div>
          <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Restricted review area.</h1>
          <p className="text-xs mt-2 max-w-2xl" style={{ color: "var(--text-muted)" }}>
            Alerts are surfaced only when an automated screener flags a possible safety concern in a member's journal.
            Full journal history is never exposed here. All access is audited.
          </p>
        </div>
        <select data-testid="safety-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-full border px-3 py-1.5 text-xs bg-transparent" style={{ borderColor: "var(--border-cream)" }}>
          <option value="all">All</option><option value="New">New</option><option value="Reviewing">Reviewing</option><option value="Resolved">Resolved</option>
        </select>
      </div>
      <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <table className="w-full text-sm">
          <thead style={{ background: "var(--bg-cream)" }}>
            <tr className="text-left">
              {["Level","Member","When","Reason","Status"].map((h) => <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>{h}</th>)}
              <th className="px-4 py-3"/>
            </tr>
          </thead>
          <tbody>
            {alerts.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center" style={{ color: "var(--text-muted)" }}>No safety alerts.</td></tr>}
            {alerts.map((a) => (
              <tr key={a.id} data-testid={`safety-row-${a.id}`} className="border-t" style={{ borderColor: "var(--border-cream)" }}>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest"
                    style={{ background: a.level === "URGENT" ? "var(--rose-dark)" : "var(--gold)", color: a.level === "URGENT" ? "var(--bg-cream)" : "var(--burgundy)" }}>
                    <AlertTriangle className="w-3 h-3" />{a.level}
                  </span>
                </td>
                <td className="px-4 py-3">{a.member_name} <span style={{ color: "var(--text-muted)" }}>({a.member_email})</span></td>
                <td className="px-4 py-3">{(a.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3">{a.reason}</td>
                <td className="px-4 py-3">{a.status}</td>
                <td className="px-4 py-3 text-right"><button data-testid={`safety-open-${a.id}-btn`} onClick={() => openAlert(a)} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Drawer open={!!open} onClose={() => setOpen(null)} title={open ? `${open.level} alert · ${open.member_name}` : ""}>
        {open && (
          <div>
            <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
              {open.member_email} · flagged {(open.created_at || "").slice(0, 16).replace("T", " ")}
            </div>
            <div className="rounded-lg border p-4 mb-4" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold-soft)" }}>
              <div className="eyebrow mb-2">Flagged passage (minimum necessary context)</div>
              <p className="font-serif italic text-sm whitespace-pre-line" style={{ color: "var(--text-obsidian)" }}>"{open.excerpt}"</p>
              <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>Reason: {open.reason}</div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {["New", "Reviewing", "Resolved"].map((s) => (
                <button key={s} data-testid={`safety-status-${s.toLowerCase()}-btn`} onClick={() => changeStatus(s)}
                  className="rounded-full px-3 py-1.5 text-xs font-medium border"
                  style={{ borderColor: open.status === s ? "var(--burgundy)" : "var(--border-cream)", background: open.status === s ? "var(--burgundy)" : "transparent", color: open.status === s ? "var(--bg-cream)" : "var(--burgundy)" }}>
                  {s}
                </button>
              ))}
            </div>
            <label className={labelCls} style={{ color: "var(--gold-dark)" }}>Reviewer notes / actions taken</label>
            <textarea data-testid="safety-notes-input" rows={3} defaultValue={open.notes || ""}
              onBlur={(e) => { if (e.target.value !== (open.notes || "")) addNote(e.target.value); }}
              className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
            <div className="hairline my-4" />
            <div className="eyebrow mb-2">Access audit log</div>
            <ul className="text-xs space-y-1" style={{ color: "var(--text-muted)" }}>
              {audit.map((a) => (
                <li key={a.id}>{(a.created_at || "").slice(0, 16).replace("T", " ")} — {a.admin_name} · {a.action}{a.detail ? ` · ${a.detail}` : ""}</li>
              ))}
              {audit.length === 0 && <li>No prior access recorded.</li>}
            </ul>
          </div>
        )}
      </Drawer>
    </div>
  );
}

// -------------------- Radio --------------------
function RadioPanel() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/radio-config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const save = async () => {
    const { id, created_at, updated_at, ...body } = cfg;
    await api.patch("/admin/radio-config", body);
    toast("Radio configuration saved");
  };
  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-1">Admin · Radio</div>
        <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Temple Radio configuration.</h1>
        <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>Temple Radio streams on Citrus3. The Listen Live player will play the URL you configure here.</p>
      </div>
      <div className="rounded-2xl border p-6 max-w-2xl" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        <Field label="Station name"><input data-testid="radio-name-input" value={cfg.station_name || ""} onChange={(e) => setCfg({ ...cfg, station_name: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        <Field label="Stream URL (Citrus3)"><input data-testid="radio-stream-input" value={cfg.stream_url || ""} onChange={(e) => setCfg({ ...cfg, stream_url: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        <Field label="Now Playing metadata URL"><input value={cfg.now_playing_url || ""} onChange={(e) => setCfg({ ...cfg, now_playing_url: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        <Field label="Station description"><textarea rows={3} value={cfg.description || ""} onChange={(e) => setCfg({ ...cfg, description: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        <Field label="Schedule (free text)"><textarea rows={4} value={cfg.schedule_text || ""} onChange={(e) => setCfg({ ...cfg, schedule_text: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        <Field label="Station artwork URL"><input value={cfg.art_url || ""} onChange={(e) => setCfg({ ...cfg, art_url: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        <label className="inline-flex items-center gap-2 text-sm mb-4">
          <input type="checkbox" data-testid="radio-live-toggle" checked={!!cfg.listen_live_enabled} onChange={(e) => setCfg({ ...cfg, listen_live_enabled: e.target.checked })} />
          Enable "Listen Live" on the public Radio page
        </label>
        <button data-testid="radio-save-btn" onClick={save} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save configuration</button>
      </div>
      <RadioSchedulePanel />
    </div>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EMPTY_BLOCK = { days: [], start_time: "19:00", end_time: "22:00", time_zone: "", title: "Temple Radio Live", description: "", active: true, order: 0 };

function RadioSchedulePanel() {
  const [blocks, setBlocks] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/content/radio_schedule").then((r) => setBlocks((r.data || []).sort((a, b) => (a.order || 0) - (b.order || 0)))), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!edit.days.length || !edit.start_time || !edit.end_time || !edit.title) { toast.error("Choose at least one day, times and a title."); return; }
    try {
      const { id, created_at, updated_at, ...body } = edit;
      if (id) await api.patch(`/admin/content/radio_schedule/${id}`, body); else await api.post("/admin/content/radio_schedule", body);
      toast("Schedule saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggle = async (b) => { await api.patch(`/admin/content/radio_schedule/${b.id}`, { active: !b.active }); load(); };
  const remove = async (b) => { if (!window.confirm("Delete this schedule block?")) return; await api.delete(`/admin/content/radio_schedule/${b.id}`); load(); };
  const fmt = (t) => { const [h, m] = (t || "").split(":").map(Number); if (isNaN(h)) return t; return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`; };
  return (
    <div className="rounded-2xl border p-6 max-w-2xl mt-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="radio-schedule-panel">
      <div className="flex items-center justify-between mb-4">
        <div><div className="eyebrow">Broadcast schedule</div><p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Shown on Listen → Radio. Active blocks only.</p></div>
        <button data-testid="radio-schedule-add-btn" onClick={() => setEdit({ ...EMPTY_BLOCK, order: blocks.length })} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}><Plus className="w-3 h-3" /> Add block</button>
      </div>
      {blocks.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>No schedule blocks yet.</div>}
      <ul className="divide-y" style={{ borderColor: "var(--border-cream)" }}>
        {blocks.map((b) => (
          <li key={b.id} className="py-3 flex items-center gap-3 text-sm" data-testid={`radio-schedule-row-${b.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-serif" style={{ color: "var(--burgundy)" }}>{b.title} {!b.active && <span className="text-[10px] font-mono uppercase ml-2" style={{ color: "var(--text-muted)" }}>archived</span>}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{(b.days || []).join(" · ")} · {fmt(b.start_time)} – {fmt(b.end_time)}{b.time_zone ? ` ${b.time_zone}` : ""}</div>
            </div>
            <button data-testid={`radio-schedule-edit-${b.id}-btn`} onClick={() => setEdit({ ...EMPTY_BLOCK, ...b })} className="p-2 rounded-full hover:bg-[color:var(--burgundy-tint)]"><Edit3 className="w-4 h-4" /></button>
            <button data-testid={`radio-schedule-toggle-${b.id}-btn`} onClick={() => toggle(b)} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>{b.active ? "Archive" : "Activate"}</button>
            <button data-testid={`radio-schedule-delete-${b.id}-btn`} onClick={() => remove(b)} className="p-2 rounded-full hover:bg-[color:var(--burgundy-tint)]"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      <Drawer open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Edit schedule block" : "New schedule block"}>
        {edit && (
          <div>
            <Field label="Program / title"><input data-testid="radio-schedule-title-input" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <div className="mb-3"><span className={labelCls} style={{ color: "var(--gold-dark)" }}>Days</span>
              <div className="flex flex-wrap gap-2">{DAYS.map((d) => <button key={d} type="button" data-testid={`radio-schedule-day-${d}-btn`} onClick={() => setEdit({ ...edit, days: edit.days.includes(d) ? edit.days.filter((x) => x !== d) : DAYS.filter((x) => [...edit.days, d].includes(x)) })} className="rounded-full px-3 py-1 text-xs border" style={{ background: edit.days.includes(d) ? "var(--burgundy)" : "transparent", color: edit.days.includes(d) ? "var(--bg-cream)" : "var(--text-obsidian)", borderColor: "var(--border-cream)" }}>{d}</button>)}</div></div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Start"><input data-testid="radio-schedule-start-input" type="time" value={edit.start_time} onChange={(e) => setEdit({ ...edit, start_time: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
              <Field label="End"><input data-testid="radio-schedule-end-input" type="time" value={edit.end_time} onChange={(e) => setEdit({ ...edit, end_time: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
              <Field label="Time zone (optional)"><input data-testid="radio-schedule-tz-input" value={edit.time_zone || ""} onChange={(e) => setEdit({ ...edit, time_zone: e.target.value })} placeholder="ET" className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            </div>
            <Field label="Description (optional)"><textarea rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Display order"><input type="number" value={edit.order} onChange={(e) => setEdit({ ...edit, order: parseInt(e.target.value || "0", 10) })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
              <label className="inline-flex items-center gap-2 text-sm mt-6"><input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Active</label>
            </div>
            <button data-testid="radio-schedule-save-btn" onClick={save} className="mt-2 rounded-full px-5 py-2 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save block</button>
          </div>
        )}
      </Drawer>
    </div>
  );
}

// -------------------- Featured --------------------
const FEATURED_SLOTS = [
  { slot: "hero_reflection",   label: "Homepage: Today's Reflection",  kind: "teaching" },
  { slot: "featured_teaching", label: "Featured Teaching",              kind: "teaching" },
  { slot: "featured_course",   label: "Featured Course",                kind: "course" },
  { slot: "featured_library",  label: "Featured Library Resource",      kind: "library" },
  { slot: "featured_gathering",label: "Featured Gathering",             kind: "event" },
];

function FeaturedPanel() {
  const [slots, setSlots] = useState({});
  const [content, setContent] = useState({ teaching: [], course: [], library: [], event: [] });

  const load = async () => {
    const [s, t, c, l, e] = await Promise.all([
      api.get("/admin/content/featured"),
      api.get("/admin/content/teachings?status=published"),
      api.get("/admin/content/courses?status=published"),
      api.get("/admin/content/library?status=published"),
      api.get("/admin/content/events?status=published"),
    ]);
    const byslot = {};
    (s.data || []).forEach((x) => byslot[x.slot] = x);
    setSlots(byslot);
    setContent({ teaching: t.data || [], course: c.data || [], library: l.data || [], event: e.data || [] });
  };
  useEffect(() => { load(); }, []);

  const setSlot = async (slot, kind, ref_id) => {
    await api.post("/admin/featured/upsert", { slot, ref_kind: kind, ref_id: ref_id || null });
    toast("Featured updated"); load();
  };

  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-1">Admin · Featured</div>
        <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Homepage curation.</h1>
        <p className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>Choose which published items appear in the featured slots on public pages.</p>
      </div>
      <div className="space-y-3">
        {FEATURED_SLOTS.map((s) => (
          <div key={s.slot} className="rounded-2xl border p-4 flex items-center gap-4" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
            <div className="flex-1">
              <div className="eyebrow mb-1">{s.slot}</div>
              <div className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>{s.label}</div>
            </div>
            <select data-testid={`featured-${s.slot}-select`} value={slots[s.slot]?.ref_id || ""}
              onChange={(e) => setSlot(s.slot, s.kind, e.target.value)}
              className={inputCls + " max-w-sm"} style={{ borderColor: "var(--border-cream)" }}>
              <option value="">— none —</option>
              {(content[s.kind] || []).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

function PodcastSyncPanel() {
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/podcast/config");
      setCfg(data);
      setUrl(data.rss_url || "");
      setLastResult(data.last_sync_result || null);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveUrl = async () => {
    try {
      await api.put("/admin/podcast/config", { rss_url: url.trim() });
      toast.success("RSS URL saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/podcast/sync", null, { timeout: 60000 });
      setLastResult(data);
      toast.success(`${data.imported} new, ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className="rounded-2xl border p-6 max-w-2xl mb-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="admin-podcast-sync-panel">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="font-serif text-xl" style={{ color: "var(--burgundy)" }}>Podcast RSS</h2>
        <span className="text-[10px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5" style={{ background: "var(--gold-soft)", color: "var(--burgundy)" }}>Auto-import</span>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
        Set the Temple podcast feed here and press <strong>Sync Podcast Feed</strong> whenever new episodes should be pulled in. Sync is idempotent — running it twice will not duplicate anything.
      </p>
      <div className={labelCls} style={{ color: "var(--gold-dark)" }}>Podcast RSS URL</div>
      <div className="flex items-center gap-2 mb-4">
        <input data-testid="admin-podcast-rss-input" className={inputCls} value={url} onChange={(e) => setUrl(e.target.value)} style={{ borderColor: "var(--border-cream)" }} />
        <button data-testid="admin-podcast-rss-save-btn" onClick={saveUrl} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save</button>
      </div>
      <button
        data-testid="admin-podcast-sync-btn"
        onClick={runSync}
        disabled={syncing}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60"
        style={{ background: "var(--gold)", color: "var(--burgundy)" }}
      >
        {syncing ? "Syncing…" : "Sync Podcast Feed"}
      </button>
      {cfg?.last_synced_at && (
        <div className="mt-4 text-xs" style={{ color: "var(--text-muted)" }} data-testid="admin-podcast-last-synced">
          Last synced: {cfg.last_synced_at.slice(0, 19).replace("T", " ")} UTC
          {lastResult && (
            <> · imported {lastResult.imported ?? 0} · updated {lastResult.updated ?? 0} · seen {lastResult.total_seen ?? 0}</>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------- Settings --------------------
function WatchersSettings() {
  const [url, setUrl] = useState("");
  const [count, setCount] = useState(null);
  useEffect(() => { api.get("/admin/watchers").then((r) => { setUrl(r.data.preview_url || ""); setCount(r.data.claimed_count); }).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/watchers/config", { preview_url: url }); toast("Watchers preview link saved (private)"); } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } };
  return (
    <div className="rounded-2xl border p-6 max-w-2xl mb-8" style={{ background: "var(--bg-cream-card)", borderColor: "var(--gold)" }} data-testid="admin-watchers-settings">
      <div className="eyebrow mb-1">Watchers Early Access</div>
      <div className="font-serif text-xl mb-3" style={{ color: "var(--burgundy)" }}>{count === null ? "" : `${count} / 100 claimed`}</div>
      <div className={labelCls} style={{ color: "var(--gold-dark)" }}>Chapters 1–5 preview link (private · only sent to claimants)</div>
      <div className="flex items-center gap-2">
        <input data-testid="admin-watchers-preview-url-input" value={url} onChange={(e) => setUrl(e.target.value)} className={inputCls} placeholder="https://docs.google.com/…" style={{ borderColor: "var(--border-cream)" }} />
        <button data-testid="admin-watchers-preview-url-save-btn" onClick={save} className="rounded-full px-4 py-2 text-xs font-medium whitespace-nowrap" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save</button>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [values, setValues] = useState({});
  useEffect(() => { api.get("/content/settings").then((r) => setValues(r.data || {})); }, []);
  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const saveOne = async (k) => {
    await api.post("/admin/settings/upsert", { key: k, value: values[k] });
    toast("Setting saved");
  };
  const KEYS = [
    ["temple_name", "Organization name"],
    ["tagline", "Tagline"],
    ["motto", "Motto"],
    ["contact_email", "Public contact email"],
    ["website", "Website / domain"],
    ["logo_url", "Logo image URL"],
    ["footer_note", "Footer text"],
    ["social_instagram", "Instagram URL"],
    ["social_youtube", "YouTube URL"],
    ["social_facebook", "Facebook URL"],
  ];
  return (
    <div>
      <PodcastSyncPanel />
      <WatchersSettings />
      <div className="mb-6">
        <div className="eyebrow mb-1">Admin · Settings</div>
        <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Temple settings.</h1>
      </div>
      <div className="rounded-2xl border p-6 max-w-2xl" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        {KEYS.map(([k, label]) => (
          <div key={k} className="mb-4">
            <div className={labelCls} style={{ color: "var(--gold-dark)" }}>{label}</div>
            <div className="flex items-center gap-2">
              {k === "motto" || k === "footer_note" ? (
                <textarea rows={2} data-testid={`setting-${k}-input`} value={values[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
              ) : (
                <input data-testid={`setting-${k}-input`} value={values[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
              )}
              <button data-testid={`setting-${k}-save-btn`} onClick={() => saveOne(k)} className="rounded-full px-3 py-1.5 text-xs font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Save</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
